// src/components/portal/SupervisorRegister.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
createUserWithEmailAndPassword,
sendEmailVerification,
deleteUser,
} from "firebase/auth";
import { auth } from "../../firebase";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../shared/legacyData/legacyCallable";
import tpcLogo from "../../images/TPC-logo-light.png";
import "./Portalauth.css";

const CONFIG = {
company: "TPC_Group",
collectionBrand: "TechPerformanceCentre",
loginRoute: "/supervisor-login",
dashboardRoute: "/supervisor-dashboard",
roleName: "Supervisor",
};

export default function SupervisorRegister() {
const inviteParams = useMemo(() => new URLSearchParams(window.location.search || ""), []);
const inviteEmail = String(inviteParams.get("email") || "").trim().toLowerCase();
const inviteSupervisorId = String(inviteParams.get("supervisorId") || "").trim();
const [email, setEmail]         = useState(inviteEmail);
const [name, setName]           = useState("");
const [surname, setSurname]     = useState("");
const [phone, setPhone]         = useState("");
const [position, setPosition]   = useState("");
const [pw, setPw]               = useState("");
const [confirmPw, setConfirmPw] = useState("");
const [terms, setTerms]         = useState(false);
const [showPw, setShowPw]       = useState(false);
const [showCPw, setShowCPw]     = useState(false);
const [busy, setBusy]           = useState(false);
const [err, setErr]             = useState("");
const [verifyStep, setVerify]   = useState(false);

const nav = useNavigate();
const fn  = useMemo(() => getFunctions(), []);
const getSystemDocs = useMemo(() => httpsCallable(fn, "getSystemDocuments"), [fn]);
const firestoreOperations = useMemo(() => httpsCallable(fn, "firestoreOperations"), [fn]);

useEffect(() => {
if (!inviteEmail && !inviteSupervisorId) return;
let cancelled = false;
(async () => {
    try {
    const res = await getSystemDocs({
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "supervisors",
        auth: true,
    });
    const payload = res?.data?.data ?? res?.data?.documents ?? res?.data;
    const all = Array.isArray(payload) ? payload : Object.values(payload || {});
    const doc = all.find((d) =>
        (inviteSupervisorId && String(d?.id || d?.uniqueId || "") === inviteSupervisorId) ||
        (inviteEmail && String(d?.email || d?.supervisorEmail || "").trim().toLowerCase() === inviteEmail)
    );
    if (!doc || cancelled) return;
    setEmail(String(doc.email || inviteEmail || "").trim().toLowerCase());
    setName((current) => current || doc.name || "");
    setSurname((current) => current || doc.surname || "");
    setPhone((current) => current || doc.phone || "");
    setPosition((current) => current || doc.position || "Supervisor");
    } catch (_) {
    // The normal submit path still validates the supervisor record.
    }
})();
return () => { cancelled = true; };
}, [getSystemDocs, inviteEmail, inviteSupervisorId]);

const handleRegister = async (e) => {
e.preventDefault();
setErr("");
if (!terms)            return setErr("Please accept Terms & Privacy.");
if (!email || !pw || !confirmPw) return setErr("All fields are required.");
if (pw.length < 6)     return setErr("Password must be 6+ characters.");
if (pw !== confirmPw)  return setErr("Passwords do not match.");

setBusy(true);
let firebaseUser = null;
try {
    // 1. Check whether this supervisor already has a profile
    let supervisorDoc = null;
    try {
    const res = await getSystemDocs({
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "supervisors",
        filters: [{ field: "email", operator: "==", value: email.trim().toLowerCase() }],
        auth: true,
    });
    const payload = res?.data?.data ?? res?.data?.documents ?? res?.data;
    const docs = Array.isArray(payload) ? payload : Object.values(payload || {});
    supervisorDoc = docs[0] || null;
    } catch (_) { /* collection may be empty */ }

    if (!supervisorDoc && inviteSupervisorId) {
    try {
        const res3 = await getSystemDocs({
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "supervisors",
        auth: true,
        });
        const payload = res3?.data?.data ?? res3?.data?.documents ?? res3?.data;
        const all = Array.isArray(payload) ? payload : Object.values(payload || {});
        supervisorDoc = all.find((d) => String(d?.id || d?.uniqueId || "") === inviteSupervisorId) || null;
    } catch (_) { /* ignore */ }
    }

    if (!supervisorDoc) {
    // Fallback: fetch all and match by email
    try {
        const res2 = await getSystemDocs({
        company: CONFIG.company,
        collectionBrand: CONFIG.collectionBrand,
        systemName: "supervisors",
        auth: true,
        });
        const payload = res2?.data?.data ?? res2?.data?.documents ?? res2?.data;
        const all = Array.isArray(payload) ? payload : Object.values(payload || {});
        supervisorDoc = all.find(
        (d) => (d.email || d.supervisorEmail || "").toLowerCase() === email.trim().toLowerCase()
        ) || null;
    } catch (_) { /* ignore */ }
    }

    if (!supervisorDoc) {
    throw new Error("No supervisor invite was found for this email address. Please use the link sent to your email or contact the funder administrator.");
    }

    if (supervisorDoc?.authUid) {
    throw new Error("This supervisor account is already registered. Please log in instead.");
    }

    // 2. Create Firebase Auth account
    const { user } = await createUserWithEmailAndPassword(auth, email.trim(), pw);
    firebaseUser = user;

    const safeEmail = email.trim().toLowerCase();
    const supervisorId = supervisorDoc?.id || supervisorDoc?.uniqueId || `supervisor_${Date.now()}`;
    const nextSupervisorDoc = {
    ...(supervisorDoc || {}),
    id: supervisorId,
    name: name.trim() || supervisorDoc?.name || "",
    surname: surname.trim() || supervisorDoc?.surname || "",
    email: safeEmail,
    phone: phone.trim() || supervisorDoc?.phone || "",
    position: position.trim() || supervisorDoc?.position || "Supervisor",
    authUid: user.uid,
    learners: Array.isArray(supervisorDoc?.learners) ? supervisorDoc.learners : [],
    funderId: supervisorDoc?.funderId || "",
    funderName: supervisorDoc?.funderName || "",
    updatedAt: new Date().toISOString(),
    createdAt: supervisorDoc?.createdAt || new Date().toISOString(),
    };

    await firestoreOperations({
    operation: "write",
    company: CONFIG.company,
    collectionBrand: CONFIG.collectionBrand,
    systemName: "supervisors",
    uniqueId: supervisorId,
    documentData: nextSupervisorDoc,
    auth: true,
    });

    // 3. Send verification email
    await sendEmailVerification(user);

    // 4. Store portal session
    sessionStorage.setItem("portalRole", "supervisor");
    sessionStorage.setItem("portalEmail", safeEmail);
    sessionStorage.setItem("portalSupervisorId", supervisorId);
    sessionStorage.setItem("portalSupervisorDoc", JSON.stringify(nextSupervisorDoc));

    setVerify(true);
} catch (ex) {
    if (firebaseUser) {
    try { await deleteUser(firebaseUser); } catch (_) { /* ignore */ }
    }
    if (ex.code === "auth/email-already-in-use") {
    setErr("An account with this email already exists. Please log in instead.");
    } else if (ex.code === "auth/invalid-email") {
    setErr("Please enter a valid email address.");
    } else {
    setErr(ex.message || "Registration failed. Please try again.");
    }
} finally {
    setBusy(false);
}
};

const EyeIcon = ({ open }) => open ? (
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
</svg>
) : (
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
</svg>
);

return (
<div className="ar-container">
    {/* Left banner */}
    <div className="ar-left">
    <div className="ar-logo-box">
        <img src={tpcLogo} alt="TPC" className="ar-logo" />
    </div>
    <div className="ar-banner-text">Supervisor Portal</div>
    </div>

    {/* Right pane */}
    <div className="ar-right">
    {verifyStep ? (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"60px 40px", textAlign:"center", background:"linear-gradient(135deg,#f8fafc 0%,#fff 100%)", borderRadius:"16px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", border:"1px solid #e5e7eb" }}>
        <div style={{ width:"80px", height:"80px", borderRadius:"50%", background:"linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"24px" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
            </svg>
        </div>
        <h3 style={{ color:"#1f3864", marginBottom:"12px" }}>Verify your email</h3>
        <p style={{ fontSize:"15px", color:"#6b7280", lineHeight:"1.6", margin:"0 0 16px 0", maxWidth:"400px" }}>
            We've sent a verification link to <strong>{email}</strong>.<br/>
            Please check your inbox and click the link to activate your Supervisor account.
            <span style={{ fontSize:"14px", color:"#1e3a8a", fontWeight:"600", backgroundColor:"#dbeafe", padding:"8px 12px", borderRadius:"6px", display:"inline-block", marginTop:"12px" }}>
            💡 Don't forget to check your spam folder!
            </span>
        </p>
        <button
            className="ar-button"
            style={{ maxWidth:"260px", marginTop:"8px" }}
            onClick={() => nav(CONFIG.loginRoute)}
        >
            Go to Supervisor Login
        </button>
        </div>
    ) : (
        <form className="ar-form" onSubmit={handleRegister}>
        <h2 className="ar-title">Create Supervisor Account</h2>
        {err && <p className="ar-err">{err}</p>}

        <input
            type="text"
            placeholder="First Name"
            className="ar-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
        />

        <input
            type="text"
            placeholder="Last Name"
            className="ar-input"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            required
        />

        <input
            type="email"
            placeholder="Email"
            className="ar-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={Boolean(inviteEmail)}
            required
        />

        <input
            type="tel"
            placeholder="Phone Number"
            className="ar-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
        />

        <input
            type="text"
            placeholder="Position / Title"
            className="ar-input"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
        />

        <div style={{ position:"relative", width:"100%" }}>
            <input
            type={showPw ? "text" : "password"}
            placeholder="Enter your password"
            className="ar-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required
            style={{ paddingRight:"45px" }}
            />
            <button type="button" onClick={() => setShowPw(!showPw)}
            style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:"4px", display:"flex", alignItems:"center", color:"#6b7280" }}>
            <EyeIcon open={showPw} />
            </button>
        </div>

        <div style={{ position:"relative", width:"100%" }}>
            <input
            type={showCPw ? "text" : "password"}
            placeholder="Confirm your password"
            className="ar-input"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            style={{ paddingRight:"45px" }}
            />
            <button type="button" onClick={() => setShowCPw(!showCPw)}
            style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:"4px", display:"flex", alignItems:"center", color:"#6b7280" }}>
            <EyeIcon open={showCPw} />
            </button>
        </div>

        <div className="ar-check-wrap">
            <input
            type="checkbox"
            id="terms-cb"
            className="ar-check"
            checked={terms}
            onChange={(e) => setTerms(e.target.checked)}
            required
            />
            <label htmlFor="terms-cb" className="ar-check-label" style={{ cursor:"pointer" }}>
            I agree to&nbsp;
            <a href="https://techperformancecentre.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</a>
            &nbsp;and&nbsp;
            <a href="https://techperformancecentre.com/terms-and-conditions" target="_blank" rel="noopener noreferrer">Privacy</a>.
            </label>
        </div>

        <div style={{ maxHeight: !terms ? "50px" : "0", overflow:"hidden", transition:"max-height 0.4s cubic-bezier(0.4,0,0.2,1), margin 0.4s", marginBottom: !terms ? "10px" : "0", opacity: !terms ? 1 : 0 }}>
            <p style={{ color:"#f59e0b", fontSize:"0.85rem", textAlign:"center", margin:"0", fontWeight:"500" }}>
            ⚠️ Please accept Terms &amp; Privacy to continue
            </p>
        </div>

        <button type="submit" className="ar-button" disabled={!terms || busy}>
            {busy ? (
            <div className="ar-button-loader">
                <div className="ar-spinner" />
                <span>Creating account…</span>
            </div>
            ) : "Create Supervisor Account"}
        </button>

        <p className="ar-small">
            Already have an account?&nbsp;<Link to={CONFIG.loginRoute}>Log in</Link>
        </p>

        <p className="ar-small" style={{ marginTop:"8px" }}>
            <Link to="/" style={{ color:"#9ca3af" }}>← Back to home</Link>
        </p>
        </form>
    )}
    </div>
</div>
);
}
